import classNames from "classnames"

import Badge from "@components/elements/V2/Badge"
import PillButton from "@components/elements/V2/Buttons/PillButton"
import TextButton from "@components/elements/V2/Buttons/TextButton"
import GatsbyStoryblokImage from "@components/elements/V2/GatsbyStoryblokImage"
import NumberDisplay from "@components/elements/V2/NumberDisplay"
import Section from "@components/elements/V2/Section"
import Typography from "@components/elements/V2/Typography"
import Video from "@components/experiments/Video"
import HeroLayout from "@components/V2/Heroes/HeroLayout"
import { Color } from "@constants/V2/color"
import { useElementObserver } from "@hooks/V2/useElementObserver"
import { getPaymentStrip, PaymentStrips } from "@utils/getPaymentStrip"
import { usePageLayoutState } from "@utils/layout"
import { getAccentColor, getBackgroundColorClass } from "@utils/V2/color"
import React, { useEffect, useRef } from "react"

type Props = {
  backgroundColor: Color
  cardColor?: Color
  secondaryButtonText: string
  secondaryButtonLink: string | YocoCom.YocoLink
  videoUrl: string
  backgroundImageUrl?: string
  backgroundImageAlt?: string
  mobileImageUrl?: string
  mobileImageAlt?: string
  coverImageUrl: string
  coverImageAlt: string
  paymentStrips: PaymentStrips
  badgeText?: string
  badgeAccentColor?: Color
  productName: string
  productDescription: string
  priceValue?: string | number
  priceAdornmentText?: string
  priceSubscriptText?: string
  priceSideScriptText?: string
  hideMobileStickyButton?: boolean
  primaryButtonText: string
  primaryButtonLink?: string | YocoCom.YocoLink
  textColor?: Color
  lightPaymentStripOverride?: { filename?: string }
  priceSuperscriptText?: string
}

const ProductBorderedHero = ({
  textColor = Color.White,
  backgroundColor,
  cardColor = Color.LightGray,
  secondaryButtonText,
  secondaryButtonLink,
  videoUrl,
  backgroundImageUrl,
  backgroundImageAlt,
  mobileImageUrl,
  mobileImageAlt,
  coverImageUrl,
  coverImageAlt,
  paymentStrips,
  productName,
  productDescription,
  priceValue,
  priceAdornmentText,
  priceSubscriptText,
  priceSideScriptText,
  badgeText,
  badgeAccentColor,
  hideMobileStickyButton,
  primaryButtonText,
  primaryButtonLink,
  lightPaymentStripOverride,
  priceSuperscriptText,
  ...props
}: Props) => {
  const ctaContainerRef = useRef(null)
  const { isVisible: isCtaVisible, startObserving } =
    useElementObserver(ctaContainerRef)

  startObserving()

  const { setHeroCTAVisible, hasAnchorNavigation } = usePageLayoutState()
  useEffect(() => {
    setHeroCTAVisible(isCtaVisible)
    return () => setHeroCTAVisible(false)
  }, [isCtaVisible, setHeroCTAVisible])

  const paymentStripMobile = getPaymentStrip(
    paymentStrips,
    backgroundColor,
    lightPaymentStripOverride
  )

  const paymentStripDesktop = getPaymentStrip(
    paymentStrips,
    getAccentColor(textColor),
    lightPaymentStripOverride
  )

  return (
    <HeroLayout
      primaryButtonText={primaryButtonText}
      primaryButtonLink={primaryButtonLink}
      primaryButtonMobileLink={primaryButtonLink}
      hideMobileButton={hideMobileStickyButton}
      primaryButtonColor={Color.Blue}
    >
      <Section
        className={classNames(
          "-mb-lg flex items-center justify-center pb-32 sm:pb-40",
          hasAnchorNavigation ? "pt-12 sm:pt-navHeight-xs" : "pt-navHeight-xs",
          getBackgroundColorClass(backgroundColor)
        )}
        {...props}
      >
        <div
          ref={ctaContainerRef}
          className={classNames(
            "relative mt-0 flex w-full flex-row justify-between overflow-hidden rounded-20 max-sm:bg-white md:mt-20",
            getBackgroundColorClass(cardColor)
          )}
        >
          {backgroundImageUrl && (
            <div className="absolute left-0 top-0 hidden h-full w-full sm:block">
              <GatsbyStoryblokImage
                image={backgroundImageUrl}
                alt={backgroundImageAlt || "Background image"}
                loadType="eager"
                fetchPriority="high"
                placeholderColor={cardColor}
                quality={80}
                aspectRatio="fullWidth"
                rounded
                className="h-full w-auto"
              />
            </div>
          )}

          {/* desktop */}
          <div className="z-10 hidden flex-col items-start gap-[25px] sm:flex sm:max-w-[460px] sm:px-48 sm:py-48 md:max-w-[550px] md:px-[70px] md:py-60 lg:max-w-[600px] lg:pb-56 lg:pt-[125.5px]">
            {badgeText && (
              <Badge
                text={badgeText}
                textColor={getAccentColor(badgeAccentColor || Color.Orange)}
                accentColor={badgeAccentColor || Color.White}
                style="solid"
              />
            )}

            <div className="flex flex-col gap-10">
              <Typography
                text={productName}
                font="grotesk"
                weight="medium"
                size="h4"
                color={textColor}
              />
              <Typography
                text={productDescription}
                font="grotesk"
                weight="book"
                size="body-md"
                color={textColor}
              />
            </div>

            <NumberDisplay
              type="price"
              size="matchFont"
              valueFontSize="h4"
              value={priceValue}
              superscriptText={priceSuperscriptText}
              adornmentText={priceAdornmentText || "R"}
              strikeSuperscriptText={false}
              adornmentTextPosition="left"
              textColor={textColor}
              subscriptText={priceSubscriptText}
              sideScriptText={priceSideScriptText}
              sideScriptTextColor={Color.Orange}
              strikeSideScriptText={true}
            />

            <div className="flex flex-row items-center gap-32">
              <PillButton
                size="medium"
                style="solid"
                text={primaryButtonText}
                color={Color.Blue}
                linkUrl={primaryButtonLink}
                className="!w-[220px] !py-14"
              />
              <TextButton
                style="text"
                text={secondaryButtonText}
                linkUrl={secondaryButtonLink}
                color={textColor}
                textSize="body-md"
                className="sm:blocksm:pt-8 hidden"
              />
            </div>

            {paymentStripDesktop?.filename && (
              <div className="mt-72 flex flex-col items-start gap-16 pb-0 sm:pb-20">
                <Typography
                  text="Accepts major credit cards"
                  font="grotesk"
                  weight="book"
                  size="body-sm"
                  color={textColor}
                  className="opacity-50"
                />
                <GatsbyStoryblokImage
                  loadType="eager"
                  image={paymentStripDesktop?.filename}
                  alt="Credit cards"
                  className="!hidden h-20 w-auto sm:!block"
                />
              </div>
            )}
          </div>

          {videoUrl && (
            <div className="hidden items-center justify-center sm:flex">
              <Video
                video={videoUrl}
                coverImage={coverImageUrl}
                ImageAlt={coverImageAlt}
                loadType="eager"
                aspectRatio="1:1"
                playbackMode="autoplay"
                width={600}
              />
            </div>
          )}

          {/* mobile */}
          <div className="z-10 flex w-full flex-col gap-[25px] py-24 sm:hidden">
            <div className="flex flex-col gap-10">
              {badgeText && (
                <div className="flex">
                  <Badge
                    text={badgeText}
                    textColor={getAccentColor(badgeAccentColor || Color.Orange)}
                    accentColor={badgeAccentColor || Color.White}
                    style="solid"
                  />
                </div>
              )}
              <Typography
                text={productName}
                font="grotesk"
                weight="medium"
                size="h4"
                color={getAccentColor(backgroundColor)}
              />
              <Typography
                text={productDescription}
                font="grotesk"
                weight="book"
                size="body-md"
                color={getAccentColor(backgroundColor)}
              />
            </div>

            <NumberDisplay
              type="price"
              size="matchFont"
              valueFontSize="h3"
              value={priceValue}
              superscriptText={priceSuperscriptText}
              adornmentText={priceAdornmentText || "R"}
              strikeSuperscriptText={false}
              adornmentTextPosition="left"
              textColor={getAccentColor(backgroundColor)}
              subscriptText={priceSubscriptText}
              sideScriptText={priceSideScriptText}
              sideScriptTextColor={Color.Orange}
              strikeSideScriptText={true}
            />

            <div className="flex flex-col items-center gap-16">
              <PillButton
                size="medium"
                style="solid"
                text={primaryButtonText}
                color={Color.Blue}
                linkUrl={primaryButtonLink}
                className="!w-[220px] !py-14"
              />
              <TextButton
                style="text"
                text={secondaryButtonText}
                linkUrl={secondaryButtonLink}
                color={getAccentColor(backgroundColor)}
                textSize="body-md"
              />
            </div>

            {mobileImageUrl && (
              <GatsbyStoryblokImage
                image={mobileImageUrl}
                alt={mobileImageAlt || "Mobile image"}
                loadType="eager"
                fetchPriority="high"
                placeholderColor={Color.White}
                aspectRatio="1:1"
                className="rounded-12"
              />
            )}

            {videoUrl && (
              <Video
                video={videoUrl}
                coverImage={coverImageUrl}
                ImageAlt={coverImageAlt}
                loadType="eager"
                aspectRatio="1:1"
                playbackMode="autoplay"
                width={600}
                playOnMobile
                className="rounded-12"
              />
            )}

            {paymentStripMobile?.filename && (
              <div className="mt-72 flex flex-col items-center gap-16">
                <Typography
                  text="Accepts major credit cards"
                  font="grotesk"
                  weight="book"
                  size="body-sm"
                  color={getAccentColor(backgroundColor)}
                  className="opacity-50"
                />
                <GatsbyStoryblokImage
                  loadType="eager"
                  image={paymentStripMobile.filename}
                  alt="Credit cards"
                  className="block h-20 w-auto sm:!hidden"
                />
              </div>
            )}
          </div>
        </div>
      </Section>
    </HeroLayout>
  )
}

export default ProductBorderedHero
